import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCustomProduct, createCustomProduct, updateCustomProduct, getCategories } from '../lib/api';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const statuses = [
    { value: 'inquiry', label: 'Inquiry' },
    { value: 'under_development', label: 'Under Development' },
    { value: 'sample_stage', label: 'Sample Stage' },
    { value: 'quoted', label: 'Quoted' },
    { value: 'approved', label: 'Approved' },
    { value: 'converted_to_standard', label: 'Converted to Standard' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'dormant', label: 'Dormant' },
];

const customTypes = [
    { value: 'modified_catalog', label: 'Modified Catalog' },
    { value: 'fully_new', label: 'Fully New' },
    { value: 'quote_only', label: 'Quote Only' },
];

function Field({ label, name, value, onChange, type = 'text', placeholder, mono, className = '' }) {
    return (
        <div className={className}>
            <Label htmlFor={name} className="text-sm text-muted-foreground mb-1.5 block">{label}</Label>
            <Input id={name} name={name} type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder || label} className={mono ? 'font-mono' : ''} data-testid={`field-${name}`} />
        </div>
    );
}

function TextArea({ label, name, value, onChange, rows = 3 }) {
    return (
        <div className="md:col-span-2">
            <Label htmlFor={name} className="text-sm text-muted-foreground mb-1.5 block">{label}</Label>
            <textarea id={name} name={name} value={value ?? ''} onChange={onChange} rows={rows}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`field-${name}`} />
        </div>
    );
}

export default function CustomProductForm() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(productId);

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({
        custom_code: '', custom_type: 'fully_new', base_product_key: '', customer_specific_flag: false,
        category: '', customer_name: '', inquiry_reference: '', product_name: '',
        dimensions: '', thickness: '', decor_no: '', decor_name: '', finish: '', shape: '',
        groove_count: '', bowl_count: '', handle_type: '', packaging_notes: '',
        costing: '', quoted_price: '', remarks: '', status: 'inquiry',
    });

    useEffect(() => {
        getCategories().then(r => {
            const cats = Array.isArray(r.data) ? r.data.map(c => typeof c === 'string' ? c : c.category) : [];
            setCategories([...new Set(cats.filter(Boolean))]);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await getCustomProduct(productId);
                setForm(prev => {
                    const p = res.data;
                    const updated = {};
                    Object.keys(prev).forEach(k => { updated[k] = p[k] ?? ''; });
                    return updated;
                });
            } catch { toast.error('Failed to load'); navigate('/custom-products'); }
            finally { setLoading(false); }
        })();
    }, [productId, isEdit, navigate]);

    const onChange = e => { const { name, value } = e.target; setForm(p => ({ ...p, [name]: value })); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.product_name.trim()) { toast.error('Product name required'); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            ['costing', 'quoted_price'].forEach(f => { payload[f] = payload[f] !== '' ? parseFloat(payload[f]) || null : null; });
            ['groove_count', 'bowl_count'].forEach(f => { payload[f] = payload[f] !== '' ? parseInt(payload[f]) || null : null; });
            
            if (isEdit) { await updateCustomProduct(productId, payload); toast.success('Updated'); }
            else { await createCustomProduct(payload); toast.success('Created'); }
            navigate('/custom-products');
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;

    return (
        <div className="space-y-6 max-w-5xl" data-testid="custom-product-form">
            <div>
                <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
                    <Link to="/custom-products"><ArrowLeft size={16} className="mr-1" />Back</Link>
                </Button>
                <h1 className="text-2xl font-bold tracking-tight">{isEdit ? `Edit: ${form.custom_code || 'Custom Product'}` : 'New Custom Product'}</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3.5 border-b border-border"><h3 className="font-semibold text-sm">Classification</h3></div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <Field label="Custom Code" name="custom_code" value={form.custom_code} onChange={onChange} mono placeholder="Auto-generated if blank" />
                        <div>
                            <Label className="text-sm text-muted-foreground mb-1.5 block">Type</Label>
                            <Select value={form.custom_type} onValueChange={v => setForm(p => ({ ...p, custom_type: v }))}>
                                <SelectTrigger data-testid="field-custom_type"><SelectValue /></SelectTrigger>
                                <SelectContent>{customTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <Field label="Base Catalog Product Key" name="base_product_key" value={form.base_product_key} onChange={onChange} mono placeholder="Optional link" />
                        <div>
                            <Label className="text-sm text-muted-foreground mb-1.5 block">Status</Label>
                            <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                                <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
                                <SelectContent>{statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <Field label="Customer Name" name="customer_name" value={form.customer_name} onChange={onChange} />
                        <Field label="Inquiry Reference" name="inquiry_reference" value={form.inquiry_reference} onChange={onChange} />
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3.5 border-b border-border"><h3 className="font-semibold text-sm">Product Details</h3></div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <Field label="Product Name" name="product_name" value={form.product_name} onChange={onChange} className="md:col-span-2" />
                        <div>
                            <Label className="text-sm text-muted-foreground mb-1.5 block">Category</Label>
                            <Select value={form.category || "none"} onValueChange={v => setForm(p => ({ ...p, category: v === "none" ? '' : v }))}>
                                <SelectTrigger data-testid="field-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No category</SelectItem>
                                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <Field label="Dimensions" name="dimensions" value={form.dimensions} onChange={onChange} mono />
                        <Field label="Thickness" name="thickness" value={form.thickness} onChange={onChange} mono />
                        <Field label="Decor No" name="decor_no" value={form.decor_no} onChange={onChange} mono />
                        <Field label="Decor Name" name="decor_name" value={form.decor_name} onChange={onChange} />
                        <Field label="Finish" name="finish" value={form.finish} onChange={onChange} />
                        <Field label="Shape" name="shape" value={form.shape} onChange={onChange} />
                        <Field label="Groove Count" name="groove_count" value={form.groove_count} onChange={onChange} type="number" mono />
                        <Field label="Bowl Count" name="bowl_count" value={form.bowl_count} onChange={onChange} type="number" mono />
                        <Field label="Handle Type" name="handle_type" value={form.handle_type} onChange={onChange} />
                    </div>
                </div>

                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3.5 border-b border-border"><h3 className="font-semibold text-sm">Commercial</h3></div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <Field label="Costing (EUR)" name="costing" value={form.costing} onChange={onChange} type="number" mono />
                        <Field label="Quoted Price (EUR)" name="quoted_price" value={form.quoted_price} onChange={onChange} type="number" mono />
                        <TextArea label="Packaging Notes" name="packaging_notes" value={form.packaging_notes} onChange={onChange} />
                        <TextArea label="Remarks" name="remarks" value={form.remarks} onChange={onChange} />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Button type="submit" disabled={saving} data-testid="save-btn">
                        <FloppyDisk size={18} className="mr-2" />{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Product'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate('/custom-products')}>Cancel</Button>
                </div>
            </form>
        </div>
    );
}
